import React from 'react';
import { App } from '../../app/AppSingleton';
import { AppSignals } from '../../app/AppSignals';
import {ViewMode, ViewModeEnum} from '../../models/view-types';
import {Board, Column as ColumnType} from '../../models/types';
import { Icons } from '../../constants/icons';
import './Board.nav.css';
import {Disposer} from "../../app/Signals";

interface BoardNavProps {
	// Пропсы будут передаваться из Board компонента
	board:Board
}

interface BoardNavState {
	viewMode: ViewMode;
	columns: ColumnType[];
	currentIndex: number;
	showNavigation: boolean;
	isManualSelection: boolean;
	manualColumnId: string | null;
}

type CandidateType = {
	index: number;
	visibility: number;
	isHeaderVisible?: boolean;
	distanceToTop?: number;
};

export class BoardNav extends React.Component<BoardNavProps, BoardNavState> {
	private app = App.getInstance();
	private classBlock = 'board-nav';
	private buttonsContainerRef = React.createRef<HTMLDivElement>();
	private scrollContainer: Element | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private disposer = new Disposer();
	private scrollTimeout: NodeJS.Timeout | null = null;
	private isUpdatingScroll = false;

	constructor(props: BoardNavProps) {
		super(props);
		this.state = {
			viewMode: this.app.getCurrentViewMode(),
			columns: this.getSortedColumns(),
			currentIndex: 0,
			showNavigation: false,
			isManualSelection: false,
			manualColumnId: null,
		};
	}

	componentDidMount(): void {
		this.setupSubscriptions();
		this.setupEventListeners();
		this.checkNavigationNeeded();
	}

	componentWillUnmount(): void {
		this.cleanupSubscriptions();
		this.cleanupEventListeners();
	}
	private isColumns() {
		return this.getCurrentViewMode() === ViewModeEnum.Columns;
	}
	private isList() {
		return this.getCurrentViewMode() === ViewModeEnum.List;
	}

	// ========== Подписки ==========
	private setupSubscriptions(): void {
		this.disposer.register(AppSignals.viewModeChanged, this.handleViewModeChanged);
		this.disposer.register(AppSignals.boardUpdated, this.handleBoardUpdated);
	}

	private updateEventListeners(): void {
		// Удаляем старые слушатели
		this.cleanupEventListeners();
		// Сбрасываем scrollContainer
		this.scrollContainer = null;
		// Устанавливаем новые слушатели для текущего режима
		this.setupEventListeners();
	}

	private cleanupSubscriptions(): void {
		this.disposer.dispose();
	}

	// ========== Обработчики событий навигатора (стрелочные функции) ==========
	private handleNavScroll = (): void => {
		this.updateActiveColumnFromScroll();
	};

	// ========== Обработчики событий (стрелочные функции) ==========
	private handleViewModeChanged = (mode: ViewMode): void => {
		this.setState({ viewMode: mode }, () => {
			this.checkNavigationNeeded();
			this.updateEventListeners();
		});
	};

	private handleBoardUpdated = (): void => {
		this.setState({ columns: this.getSortedColumns() }, this.checkNavigationNeeded);
	};

	private handleColumnClick = (index: number): void => {
		if (this.isUpdatingScroll) return;

		const columnId = this.state.columns[index]?.id;
		if (!columnId) return;

		this.setState({
			currentIndex: index,
			isManualSelection: true,
			manualColumnId: columnId
		}, () => {
			this.scrollToColumn(index);
			this.centerSelectedButton(index);
		});
	};

	// ========== Вспомогательные методы (обычные методы) ==========
	private getSortedColumns(): ColumnType[] {
		const board = this.props.board;
		return [...board.columns].sort((a, b) => a.order - b.order);
	}

	private setupEventListeners(): void {
		// Находим контейнер для скролла
		this.scrollContainer = this.getScrollContainer();

		if (this.scrollContainer) {
			this.scrollContainer.addEventListener('scroll', this.handleContainerScroll);
			this.disposer.registerDisposer(() => {
				this.scrollContainer?.removeEventListener('scroll', this.handleContainerScroll);
			});
		}

		// Для режима списка также слушаем скролл window
		if (this.isList()) {
			window.addEventListener('scroll', this.handleWindowScroll);
			this.disposer.registerDisposer(() => {
				window.removeEventListener('scroll', this.handleWindowScroll);
			});
		}

		this.resizeObserver = new ResizeObserver(this.checkNavigationNeeded);
		if (this.scrollContainer) {
			this.resizeObserver.observe(this.scrollContainer);
		}
	}

	private getScrollContainer(): Element | null {
		if (this.isColumns()) {
			return document.querySelector('.board--columns .board__container') ||
				   document.querySelector('.board');
		} else {
			// Для режима списка используем window как контейнер скролла
			return document.querySelector('.board');
		}
	}

	private handleContainerScroll = (): void => {
		this.debounceUpdateActiveColumn();
	};

	private handleWindowScroll = (): void => {
		if (this.isList()) {
			this.debounceUpdateActiveColumn();
		}
	};

	private updateActiveColumn(): void {
		if (this.state.columns.length === 0) return;

		// Если включен ручной режим, проверяем видимость выбранной колонки
		if (this.state.isManualSelection && this.state.manualColumnId) {
			const isVisible = this.isColumnVisible(this.state.manualColumnId, 0.05); // 5% видимости
			if (!isVisible) {
				// Колонка уехала из видимости → переключаемся на авторежим
				this.setState({ isManualSelection: false, manualColumnId: null });
			} else {
				// Колонка еще видна → не меняем активную
				return;
			}
		}

		// Автоматическое определение в зависимости от режима
		if (this.isColumns()) {
			this.updateActiveColumnForColumns();
		} else if (this.isList()) {
			this.updateActiveColumnForList();
		}
	}

	private updateActiveColumnForColumns(): void {
		const container = this.scrollContainer || document.querySelector('.board--columns .board__container');
		if (!container) return;

		const scrollLeft = container.scrollLeft;
		
		// Получаем реальную ширину колонки с учетом gap
		const columnElements = container.querySelectorAll('.column');
		let columnWidth = container.clientWidth * 0.85; // fallback
		
		if (columnElements.length > 0 && columnElements[0]) {
			const firstColumn = columnElements[0] as HTMLElement;
			columnWidth = firstColumn.offsetWidth;
			
			// Добавляем gap между колонками
			const containerStyle = window.getComputedStyle(container);
			const gap = parseFloat(containerStyle.gap) || 0;
			columnWidth += gap;
		}
		
		const newIndex = Math.round(scrollLeft / columnWidth);

		// Проверяем границы
		if (newIndex >= 0 &&
			newIndex < this.state.columns.length &&
			newIndex !== this.state.currentIndex) {
			this.setState({ currentIndex: newIndex });
			this.centerSelectedButton(newIndex);
		}
	}

	private updateActiveColumnForList(): void {
		const boardContainer = document.querySelector('.board');
		if (!boardContainer) return;

		const boardRect = boardContainer.getBoundingClientRect();
		const boardTopThird = boardRect.height / 3; // Высота верхней трети контейнера

		const columnElements = document.querySelectorAll('.column');
		if (columnElements.length === 0) return;

		let bestCandidate: CandidateType | null = null;
		let bestScore = -1;

		columnElements.forEach((columnElement) => {
			if (!(columnElement instanceof HTMLElement) || !columnElement.id) return;

			// Находим индекс колонки в массиве columns
			const columnIndex = this.state.columns.findIndex(col => col.id === columnElement.id);
			if (columnIndex === -1) return;

			// Получаем координаты колонки относительно viewport
			const columnRect = columnElement.getBoundingClientRect();

			// Преобразуем в координаты относительно контейнера .board
			const columnTopRelative = columnRect.top - boardRect.top;
			const columnBottomRelative = columnRect.bottom - boardRect.top;
			const columnHeight = columnRect.height;

			// Вычисляем видимость в верхней трети контейнера
			const visibilityInTopThird = this.calculateVisibilityInTopThird(
				columnTopRelative,
				columnBottomRelative,
				columnHeight,
				boardTopThird
			);

			// Проверяем видимость заголовка в контейнере
			const headerElement = columnElement.querySelector('.column__header');
			let isHeaderVisible = false;

			if (headerElement) {
				const headerRect = headerElement.getBoundingClientRect();
				const headerTopRelative = headerRect.top - boardRect.top;
				const headerBottomRelative = headerRect.bottom - boardRect.top;

				// Заголовок виден, если он находится в пределах контейнера
				isHeaderVisible = headerTopRelative >= 0 &&
					headerBottomRelative <= boardRect.height &&
					headerBottomRelative > 0;
			}

			// Вычисляем общий score по приоритетам:
			// 1. Видимость в верхней трети (0-100)
			// 2. Видимость заголовка (добавляем бонус)
			// 3. Близость к верху (чем меньше расстояние, тем лучше)
			let score = visibilityInTopThird;

			if (isHeaderVisible) {
				score += 50; // Бонус за видимый заголовок
			}

			// Бонус за близость к верху (максимум 30 баллов)
			const distanceFromTop = Math.max(0, columnTopRelative);
			const proximityBonus = Math.max(0, 30 - (distanceFromTop / 50));
			score += proximityBonus;

			if (score > bestScore) {
				bestScore = score;
				bestCandidate = {
					index: columnIndex,
					visibility: visibilityInTopThird,
					isHeaderVisible,
					distanceToTop: columnTopRelative
				};
			}
		});

		if (bestCandidate && (bestCandidate as CandidateType).index !== this.state.currentIndex) {
			this.setState({ currentIndex: (bestCandidate as CandidateType).index });
			this.centerSelectedButton((bestCandidate as CandidateType).index);
		}
	}
	private isColumnVisible(columnId: string, minVisibility: number = 0.1): boolean {
		const columnElement = document.getElementById(columnId);
		if (!columnElement) return false;

		const rect = columnElement.getBoundingClientRect();
		const windowHeight = window.innerHeight;
		const windowWidth = window.innerWidth;

		if (this.isList()) {
			// Для списка: проверяем вертикальную видимость относительно .board контейнера
			const boardContainer = document.querySelector('.board');
			if (!boardContainer) return false;

			const boardRect = boardContainer.getBoundingClientRect();

			// Преобразуем в относительные координаты
			const columnTopRelative = rect.top - boardRect.top;
			const columnBottomRelative = rect.bottom - boardRect.top;
			const columnHeight = rect.height;

			// Находим пересечение с контейнером
			const intersectionTop = Math.max(columnTopRelative, 0);
			const intersectionBottom = Math.min(columnBottomRelative, boardRect.height);

			if (intersectionBottom <= intersectionTop) return false;

			const visibleHeight = intersectionBottom - intersectionTop;
			return visibleHeight >= columnHeight * minVisibility;
		} else {
			// Для колонок: проверяем горизонтальную видимость
			const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);
			return visibleWidth >= rect.width * minVisibility;
		}
	}

	private calculateVisibilityInTopThird(
		columnTop: number,
		columnBottom: number,
		columnHeight: number,
		boardTopThird: number
	): number {
		// Верхняя треть контейнера: от 0 до boardTopThird
		const topThirdStart = 0;
		const topThirdEnd = boardTopThird;

		// Находим пересечение колонки с верхней третью
		const intersectionTop = Math.max(columnTop, topThirdStart);
		const intersectionBottom = Math.min(columnBottom, topThirdEnd);

		// Если нет пересечения
		if (intersectionBottom <= intersectionTop) {
			return 0;
		}

		// Высота пересечения
		const intersectionHeight = intersectionBottom - intersectionTop;

		// Процент колонки, видимый в верхней трети контейнера
		return (intersectionHeight / columnHeight) * 100;
	}

	private debounceUpdateActiveColumn(): void {
		// Дебаунсим обновление для оптимизации
		if (this.scrollTimeout) {
			clearTimeout(this.scrollTimeout);
		}

		this.scrollTimeout = setTimeout(() => {
			this.updateActiveColumn();
		}, 150);
	}

	private updateActiveColumnFromScroll(): void {
		const buttonsContainer = this.buttonsContainerRef.current;
		if (!buttonsContainer) return;

		this.debounceUpdateActiveColumn();
	}

	private centerSelectedButton(index: number): void {
		this.isUpdatingScroll = true;

		const container = this.buttonsContainerRef.current;
		if (!container) {
			this.isUpdatingScroll = false;
			return;
		}

		const button = container.children[index] as HTMLElement;
		if (!button) {
			this.isUpdatingScroll = false;
			return;
		}

		const containerWidth = container.clientWidth;
		const buttonWidth = button.offsetWidth;
		const buttonOffset = button.offsetLeft;
		const maxScroll = container.scrollWidth - containerWidth;

		let targetScroll: number;

		// Краевые случаи
		if (index === 0) {
			targetScroll = 0; // Первая кнопка
		} else if (index === this.state.columns.length - 1) {
			targetScroll = maxScroll; // Последняя кнопка
		} else {
			// Центрирование: позиция кнопки минус половина ширины контейнера плюс половина ширины кнопки
			targetScroll = buttonOffset - (containerWidth / 2) + (buttonWidth / 2);

			// Ограничиваем значения
			targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
		}

		container.scrollTo({
			left: targetScroll,
			behavior: 'smooth'
		});
		setTimeout(() => {
			this.isUpdatingScroll = false;
		}, 300);
	}

	private cleanupEventListeners(): void {
		if (this.scrollContainer) {
			this.scrollContainer.removeEventListener('scroll', this.handleContainerScroll);
		}

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}

	private checkNavigationNeeded = (): void => {
		if (this.isList()) {
			this.setState({ showNavigation: true });
			return;
		}

		const container = document.querySelector('.board--columns .board__container');
		if (container) {
			const needsNavigation = container.scrollWidth > container.clientWidth;
			this.setState({ showNavigation: needsNavigation });
		}
	};

	private scrollToColumn(index: number): void {
		if (index < 0 || index >= this.state.columns.length) return;

		if (this.isColumns()) {
			// В режиме колонок - горизонтальный скролл
			const container = document.querySelector('.board--columns') || document.querySelector('.board--columns .board__container');
			if (!container) return;

			// Получаем реальную ширину колонки с учетом gap
			const columnElements = container.querySelectorAll('.column');
			let columnWidth = container.clientWidth * 0.85; // fallback
			
			if (columnElements.length > 0 && columnElements[0]) {
				const firstColumn = columnElements[0] as HTMLElement;
				columnWidth = firstColumn.offsetWidth;
				
				// Добавляем gap между колонками
				const containerStyle = window.getComputedStyle(container);
				const gap = parseFloat(containerStyle.gap) || 0;
				columnWidth += gap;
			}

			// Позиционируем по левой границе колонки
			const targetScroll = index * columnWidth;
			container.scrollTo({
				left: targetScroll,
				behavior: 'smooth'
			});
		} else if (this.isList()) {
			// В режиме списка - вертикальный скролл к колонке
			const columnId = this.state.columns[index]?.id;
			if (!columnId) return;

			const columnElement = document.getElementById(columnId);
			if (columnElement) {
				columnElement.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				});
			}
		}
	}


	// noinspection JSMethodCanBeStatic
	private getCurrentViewMode() :string{
		return App.getInstance().getCurrentViewMode();
	}

	private getButtonClasses(index: number): string {
		const classElement = `${this.classBlock}__button`;
		const classActive = index === this.state.currentIndex
			? `${classElement}--active`
			: `${classElement}--inactive`;

		return `${classElement} ${classActive}`.trim();
	}

	// ========== Методы рендеринга (обычные методы) ==========
	private renderColumnButtons(): React.ReactNode {
		const classElement = `${this.classBlock}__buttons`;

		return (
			<div className={classElement} ref={this.buttonsContainerRef}>
				{this.state.columns.map((column, index) => (
					<button
						key={column.id}
						onClick={() => this.handleColumnClick(index)}
						className={this.getButtonClasses(index)}
						aria-label={`Перейти к колонке ${column.title}`}
						aria-current={index === this.state.currentIndex ? 'page' : undefined}
					>
						{column.title}
					</button>
				))}
			</div>
		);
	}

	private renderContent(): React.ReactNode {
		const classElement = `${this.classBlock}__content`;

		return (
			<div className={classElement}>
				{this.renderColumnButtons()}
			</div>
		);
	}

	render(): React.ReactNode {
		const { showNavigation, columns, viewMode } = this.state;

		if (!showNavigation || columns.length === 0) {
			return null;
		}

		const classModifier = `${this.classBlock}--${viewMode}`;

		return (
			<div className={`${this.classBlock} ${classModifier}`.trim()}>
				{this.renderContent()}
			</div>
		);
	}
}